import type { CurrentSession, LoginCodeSent } from "@cleanbudget/contract/session";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Separator } from "@cleanbudget/ui/components/separator";
import { IconAlertCircle, IconFingerprint, IconLoader2, IconMail } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AuthLayout } from "@/components/auth-layout";
import { ButtonLink } from "@/components/button-link";
import { CodeStep } from "@/components/sign-in/code-step";
import { PasswordLogin } from "@/components/sign-in/password-login";
import type { OnSignedIn } from "@/components/sign-in/signed-in";
import { orpc } from "@/lib/orpc";
import { type Problem, problemOf } from "@/lib/passkey-problem";
import { safeRedirect, sessionQuery } from "@/lib/session";
import { getPasskey } from "@/lib/webauthn";

const LoginSearch = z.object({
	/** Where to go after signing in (the page that sent us here). */
	redirect: z.string().optional().catch(undefined),
	/** `password`: email and password instead of the passkey (ADR 0003). */
	method: z.enum(["password"]).optional().catch(undefined),
});

export const Route = createFileRoute("/login")({
	validateSearch: LoginSearch,
	beforeLoad: async ({ context, search }) => {
		// Signed in already: straight on.
		const session = await context.queryClient.fetchQuery(sessionQuery()).catch(() => undefined);
		if (session) throw redirect({ href: safeRedirect(search.redirect) });
	},
	component: LoginPage,
});

/**
 * Sign in with a passkey, or with email and password (`?method=password`) and then, on a browser
 * that is not trusted yet, the code from the mail.
 */
function LoginPage() {
	const { t } = useTranslation("session");
	const search = Route.useSearch();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [codeSent, setCodeSent] = useState<LoginCodeSent>();

	const signedIn = async (session: CurrentSession) => {
		queryClient.setQueryData(sessionQuery().queryKey, session);
		await navigate({ href: safeRedirect(search.redirect), replace: true });
	};

	if (codeSent) {
		return (
			<AuthLayout title={t("code.title")} description={t("code.description")}>
				<CodeStep sent={codeSent} onSignedIn={signedIn} onStartOver={() => setCodeSent(undefined)} />
			</AuthLayout>
		);
	}
	if (search.method === "password") {
		return (
			<AuthLayout title={t("title")} description={t("login.description")}>
				<PasswordLogin onSignedIn={signedIn} onCodeSent={setCodeSent} />
				<OtherMethod to={{ ...search, method: undefined }} icon={<IconFingerprint aria-hidden />}>
					{t("login.withPasskey")}
				</OtherMethod>
			</AuthLayout>
		);
	}
	return (
		<AuthLayout title={t("title")} description={t("login.description")}>
			<PasskeyLogin onSignedIn={signedIn} />
			<OtherMethod to={{ ...search, method: "password" }} icon={<IconMail aria-hidden />}>
				{t("login.withPassword")}
			</OtherMethod>
		</AuthLayout>
	);
}

/** The switch to the other way of signing in, below a line with "of". */
function OtherMethod({
	to,
	icon,
	children,
}: {
	to: z.infer<typeof LoginSearch>;
	icon: React.ReactNode;
	children: React.ReactNode;
}) {
	const { t } = useTranslation("session");
	return (
		<>
			<div className="flex items-center gap-3 text-xs text-muted-foreground">
				<Separator className="flex-1" />
				{t("login.or")}
				<Separator className="flex-1" />
			</div>
			<ButtonLink to="/login" search={to} variant="outline" size="lg">
				{icon}
				{children}
			</ButtonLink>
		</>
	);
}

/**
 * The passkey. The challenge is fetched when the page opens (and refreshed before it runs out), so
 * the click goes straight to the browser's passkey dialog.
 */
function PasskeyLogin({ onSignedIn }: { onSignedIn: OnSignedIn }) {
	const { t } = useTranslation("session");
	const [problem, setProblem] = useState<Problem>();
	const [waiting, setWaiting] = useState(false);

	const options = useQuery({
		...orpc.session.loginOptions.queryOptions(),
		staleTime: 4 * 60_000,
		refetchInterval: 4 * 60_000,
		refetchOnWindowFocus: false,
		retry: false,
	});
	const login = useMutation(orpc.session.login.mutationOptions());

	const signIn = async () => {
		if (!options.data) return;
		setProblem(undefined);
		setWaiting(true);
		try {
			const credential = await getPasskey(options.data.publicKey);
			await onSignedIn(await login.mutateAsync({ challengeId: options.data.challengeId, credential }));
		} catch (error) {
			setProblem(problemOf(error));
			// A challenge works once: the next attempt needs a new one.
			void options.refetch();
		} finally {
			setWaiting(false);
		}
	};

	// A challenge the server would not give (too many attempts) is a problem to show as well.
	const shown = problem ?? (options.error ? problemOf(options.error) : undefined);

	return (
		<>
			{shown ? (
				<Alert variant="destructive">
					<IconAlertCircle aria-hidden />
					<AlertDescription>{t(`problem.${shown}`)}</AlertDescription>
				</Alert>
			) : null}
			<Button size="lg" onClick={signIn} disabled={!options.data || waiting}>
				{waiting ? <IconLoader2 className="animate-spin" aria-hidden /> : <IconFingerprint aria-hidden />}
				{waiting ? t("login.busy") : t("login.button")}
			</Button>
		</>
	);
}
